/**
 * The vocabulary a navigation request answers with, and the single place that
 * decides what the user is told when a request did not take them anywhere.
 */

/**
 * What the user decided in front of an unsaved-changes prompt. Declared here
 * rather than imported from the dialog so this module stays free of UI.
 */
export type UnsavedChoice = 'save' | 'discard' | 'cancel';

export const NavigationOutcomes = [
  /** The destination was reached; the commit action ran exactly once. */
  'committed',
  /** The revision state does not allow this destination at all. */
  'blocked',
  /** The user chose to stay, or dismissed the prompt. */
  'cancelled',
  /** A newer revision or intent replaced this request before it committed. */
  'superseded',
  'saveFailed',
  'discardFailed',
  /** Discard was chosen with no verified snapshot to restore. */
  'noVerifiedBaseline',
] as const;

export type NavigationOutcome = (typeof NavigationOutcomes)[number];

export const isNavigationCommitted = (outcome: NavigationOutcome): boolean =>
  outcome === 'committed';

const FailedChoiceOutcome: Record<'save' | 'discard', NavigationOutcome> = {
  save: 'saveFailed',
  discard: 'discardFailed',
};

export const resolveUnsavedChoiceOutcome = (
  choice: UnsavedChoice,
  operationSucceeded: boolean
): NavigationOutcome => {
  if (choice === 'cancel') {
    return 'cancelled';
  }

  return operationSucceeded ? 'committed' : FailedChoiceOutcome[choice];
};

/**
 * Outcomes the user already understands — they either arrived, or they made the
 * decision to stay — say nothing. Everything else has to explain itself.
 */
const OutcomeMessage: Record<NavigationOutcome, string | null> = {
  committed: null,
  cancelled: null,
  superseded: null,
  saveFailed: 'Your changes could not be saved, so you are still here. Try saving again.',
  discardFailed: 'Your changes could not be discarded, so you are still here.',
  noVerifiedBaseline:
    'There is no verified setup to restore, so these changes cannot be discarded. '
    + 'Save them to continue.',
  blocked: 'This destination stays locked until the current setup is saved and verified.',
};

export const resolveNavigationOutcomeMessage = (outcome: NavigationOutcome): string | null =>
  OutcomeMessage[outcome];
